import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Heart, Sparkles, Infinity as InfinityIcon, CircleDot, MessageCircle, Eye, BookOpen } from "lucide-react";
import { Reveal } from "../components/Reveal";

const VISENT = [
  "Se libérer des schémas répétitifs et hérités",
  "Laisser les loyautés silencieuses",
  "Se libérer des blocages transgénérationnels — entraves familiales, professionnelles, liées au couple",
  "Remettre de l'ordre, de l'amour et de l'harmonie dans les systèmes",
  "Se défaire des intrications familiales",
  "Retrouver sa place dans la famille et dans sa vie",
  "Faire le deuil",
  "Se libérer des blessures et traumas de nos ancêtres",
];

const TYPES = [
  {
    icon: Heart,
    title: "Familiales",
    text: "Mettre en lumière les liens invisibles qui nous relient à notre famille : les loyautés, les secrets, les répétitions. Comprendre d'où vient ce qui se joue, pour s'en libérer en douceur.",
  },
  {
    icon: Users,
    title: "Systémiques",
    text: "Au-delà de la famille : le couple, le travail, une équipe, un projet. Tout système peut être constellé pour révéler ce qui bloque et rétablir la circulation.",
  },
  {
    icon: InfinityIcon,
    title: "Karmiques",
    text: "Pour ce qui semble se répéter au-delà de cette vie-ci : les mémoires plus anciennes, les schémas profonds. Une approche spirituelle, dans le respect des croyances de chacun.",
  },
];

const DEROULEMENT = [
  { icon: CircleDot, title: "Le cercle", text: "On s'installe en petit groupe, dans un cadre bienveillant et confidentiel. Tu déposes ta question, simplement, avec tes mots." },
  { icon: Users, title: "Les représentants", text: "Des membres du groupe incarnent les personnes ou les forces en présence. Sans rien connaître de ton histoire, ils ressentent et montrent ce qui se joue." },
  { icon: Sparkles, title: "La libération", text: "Par la parole juste et le mouvement, les nœuds se défont. Tu repars plus légère, avec une compréhension nouvelle de ta situation." },
];

export default function Constellations() {
  return (
    <div data-testid="constellations-page">
      <section className="pt-32 lg:pt-40 pb-16 px-6 lg:px-12 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-6">En groupe ou en individuel</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-ink leading-tight max-w-4xl">
            Constellations familiales, systémiques & karmiques à Bayonne
          </h1>
          <p className="mt-8 text-base sm:text-lg text-sage leading-relaxed max-w-2xl">
            Certaines choses ne se comprennent pas par la pensée : elles se montrent. En constellation, on laisse le corps et le groupe révéler ce qui se joue derrière une difficulté — pour enfin le regarder, l'apaiser et le dépasser.
          </p>
        </Reveal>
      </section>

      {/* PAVÉ EXPLICATIF */}
      <section className="pb-20 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="constellations-visent">
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-ink mb-8">Les constellations visent à…</h2>
            <ul className="space-y-4">
              {VISENT.map((v) => (
                <li key={v} className="flex items-start gap-3 text-sm sm:text-base text-sage leading-relaxed">
                  <span className="text-olive shrink-0 mt-0.5">—</span>
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <div className="lg:col-span-5 space-y-6 lg:pt-16">
            <Reveal delay={0.1}>
              <div className="bg-olive-soft/60 border border-olive-border rounded-3xl p-8">
                <Eye className="w-6 h-6 text-olive mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl sm:text-2xl font-light italic text-olive-dark leading-relaxed">
                  « Elles mettent en lumière ce qui a été oublié, les non-dits, le caché. »
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="bg-beige-50 border border-olive-border rounded-3xl p-8">
                <BookOpen className="w-6 h-6 text-olive mb-4" strokeWidth={1.5} />
                <p className="text-sm sm:text-base text-sage leading-relaxed">
                  <strong className="text-ink font-medium">Bert Hellinger</strong>, père des constellations familiales, se base sur la phénoménologie et observe le système profond de l'être humain : ce que le mental ignore, le système le sait.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TROIS APPROCHES */}
      <section className="pb-20 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8" data-testid="constellations-types">
          {TYPES.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.1}>
              <div className="h-full bg-beige-50 border border-olive-border rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1" data-testid={`constellation-${t.title.toLowerCase()}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-olive-soft border border-olive-border">
                  <t.icon className="w-5 h-5 text-olive" strokeWidth={1.5} />
                </span>
                <h2 className="mt-6 font-serif text-3xl text-ink">{t.title}</h2>
                <p className="mt-4 text-sm text-sage leading-relaxed">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GALERIE CONSTELLATIONS */}
      <section className="pb-20 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="constellations-galerie">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          <Reveal>
            <div className="group overflow-hidden rounded-3xl border border-olive-border" data-testid="galerie-salon">
              <img src="/images/constel-salon.jpg" alt="Cercle de constellation dans le salon, ambiance chaleureuse" loading="lazy" className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="group overflow-hidden rounded-3xl border border-olive-border lg:mt-16" data-testid="galerie-cercle">
              <img src="/images/constel-cercle.jpg" alt="Le cercle de parole, bougies au centre du groupe" loading="lazy" className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="group overflow-hidden rounded-3xl border border-olive-border lg:mt-8" data-testid="galerie-mains">
              <img src="/images/constel-mains.jpg" alt="Poignée de main bienveillante pendant une constellation" loading="lazy" className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-center font-serif italic text-lg sm:text-xl text-olive-dark">Des cercles qui ressemblent à ça : chaleureux, simples, humains.</p>
        </Reveal>
      </section>

      {/* EN GROUPE */}
      <section className="py-20 bg-beige-200/50 border-y border-olive-border/60" data-testid="constellations-groupe">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-olive mb-4">La constellation collective</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink max-w-2xl">En groupe : la force du cercle</h2>
            <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-2xl">
              En atelier collectif, les participants deviennent les représentants de ton système. Ce que le groupe montre est souvent saisissant de justesse — et venir représenter, sans consteller sa propre question, est déjà une expérience transformatrice.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
            {DEROULEMENT.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.1}>
                <div className="border-t-2 border-olive pt-6" data-testid={`deroulement-${i}`}>
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-olive-soft border border-olive-border">
                      <d.icon className="w-5 h-5 text-olive" strokeWidth={1.5} />
                    </span>
                    <span className="font-serif text-lg text-olive/40">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-ink">{d.title}</h3>
                  <p className="mt-3 text-sm sm:text-base text-sage leading-relaxed">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-14 bg-beige-50 border border-olive-border rounded-3xl p-8 flex flex-wrap items-center justify-between gap-6">
              <p className="text-sm sm:text-base text-sage leading-relaxed max-w-xl">
                Ateliers en petit groupe, sur une journée ou une demi-journée, à Bayonne et Bellignat. Nul besoin d'expérience. Dates et tarifs communiqués sur demande.
              </p>
              <Link
                to="/contact?soin=constellations"
                data-testid="constellations-cta-groupe"
                className="inline-flex items-center gap-2 rounded-full bg-olive px-8 py-4 text-sm font-medium text-beige-100 transition-all duration-300 hover:bg-olive-dark hover:-translate-y-0.5"
              >
                Rejoindre un atelier <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LES DEUX FORMATS */}
      <section className="py-20 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="constellations-formats">
        <Reveal className="mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-4">Deux formats, même profondeur</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink leading-tight max-w-2xl">En séance individuelle ou en groupe</h2>
          <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-2xl">
            En cabinet, à distance ou en atelier collectif : le travail est tout aussi profond, à ton rythme ou porté par le cercle. Clique sur la carte qui te correspond pour me contacter.
          </p>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-8">
          <Reveal>
            <Link
              to="/contact?soin=constellations"
              data-testid="constellations-cta-individuel"
              className="group flex h-full flex-col bg-olive-dark text-beige-100 rounded-3xl p-10 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(49,63,21,0.5)]"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-beige-300/80 mb-5">En individuel — cliquer pour entrer en contact</p>
              <p className="font-serif text-xl sm:text-2xl font-light leading-relaxed">
                En séance individuelle, nous allons pouvoir mettre en lumière — par placement d'objets qui représenteront les membres de ton système (famille, pro, amoureux…) — une vision de ton système et de ta problématique actuelle.
              </p>
              <p className="mt-5 text-sm sm:text-base text-beige-300/90 leading-relaxed">
                J'interpréterai ensuite ce qui se « joue », puis ensemble nous trouverons le meilleur emplacement pour dénouer ce système au plus juste, avec justesse et harmonie — à Bayonne et partout en France.
              </p>
              <div className="mt-8 space-y-2 text-sm">
                <p className="flex items-center justify-between border-t border-beige-100/15 pt-4"><span className="text-beige-300/90">La séance</span><span className="font-medium text-gold text-base" data-testid="tarif-individuel">90 €</span></p>
                <p className="flex items-center justify-between"><span className="text-beige-300/90">En multi-séances</span><span className="font-medium text-gold text-base">70 €</span></p>
              </div>
              <span className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-medium text-gold">
                Me contacter <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
              </span>
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact?soin=constellations"
              data-testid="constellations-cta-groupe-card"
              className="group flex h-full flex-col bg-beige-50 border border-olive-border rounded-3xl p-10 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(49,63,21,0.35)]"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-olive mb-5">En groupe — cliquer pour entrer en contact</p>
              <p className="font-serif text-xl sm:text-2xl font-light leading-relaxed text-ink">
                Tu seras observateur·rice de ta constellation en choisissant un ou plusieurs représentants de ton système, après un bref entretien pour mettre en avant la problématique du moment.
              </p>
              <p className="mt-5 text-sm sm:text-base text-sage leading-relaxed">
                Tu placeras le représentant dans le « champ qui sait » et la constellation se mettra en mouvement grâce à la phénoménologie et aux phrases réparatrices, jusqu'à ce qu'on trouve ensemble le chemin de l'équilibre.
              </p>
              <div className="mt-8 space-y-2 text-sm">
                <p className="flex items-center justify-between border-t border-olive-border pt-4"><span className="text-sage">Pour le/la constellé·e</span><span className="font-medium text-olive text-base" data-testid="tarif-groupe-constelle">70 €</span></p>
                <p className="flex items-center justify-between"><span className="text-sage">Pour le/la représentant·e</span><span className="font-medium text-olive text-base" data-testid="tarif-groupe-representant">35 €</span></p>
              </div>
              <span className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-medium text-olive">
                Me contacter <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-olive-dark text-beige-100 border-t border-beige-100/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 flex flex-wrap items-center justify-between gap-8">
          <Reveal>
            <p className="font-serif text-2xl sm:text-3xl font-light">Une question sur les constellations ?</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact?soin=constellations" data-testid="constellations-cta-bottom" className="inline-flex items-center rounded-full bg-beige-100 px-8 py-4 text-sm font-medium text-olive-dark transition-all duration-300 hover:-translate-y-0.5 hover:bg-beige-50">
                Me contacter
              </Link>
              <Link to="/ateliers" data-testid="constellations-link-ateliers" className="inline-flex items-center rounded-full border border-beige-100/40 px-8 py-4 text-sm text-beige-100 transition-all duration-300 hover:bg-beige-100/10">
                Découvrir les ateliers
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
